<script setup lang="ts">
import { computed } from 'vue'
import { useWebAppStore } from '@/stores/webApp'
import UserCard from '@/components/molecules/UserCard.vue'
import SideNavSkeleton from '@/components/molecules/SideNavSkeleton.vue'
import SideNavGroup from '@/components/molecules/SideNavGroup.vue'
import { useSideNav } from '@/composables/useSideNav'
import { useNavigation } from '@/composables/useNavigation'
import type { MenuSection } from '@/types/navigation'
import { AdsListOption } from '@accor/ads-components'

const webAppStore = useWebAppStore()
const { items, updateSelectedItem } = useNavigation()
const { isGroupOpen, onDetailToggle, menuEntriesToListOptions } = useSideNav()

const menuHasSections = computed(() => {
  return items.value.length > 0 && 'children' in items.value[0]
})

// Used for MenuEntry use case, when there is no section, only entries
const optionClicked = (index: number) => updateSelectedItem(items.value[index])
const optionsList = computed(() => menuEntriesToListOptions(items.value))
</script>

<template>
  <aside class="side-nav" :aria-busy="!webAppStore.isUserInfoReady">
    <SideNavSkeleton v-if="!webAppStore.isUserInfoReady" />

    <template v-else>
      <UserCard />
      <ul v-if="menuHasSections" class="side-nav__list" data-testid="side-nav-list">
        <li
          v-for="menuSection in items as MenuSection[]"
          :key="menuSection.i18nLabel"
          class="side-nav__list-item"
        >
          <SideNavGroup
            :menu-section="menuSection"
            :is-open="isGroupOpen(menuSection.i18nLabel)"
            @toggle="onDetailToggle"
          />
        </li>
      </ul>
      <AdsListOption v-else :options="optionsList" @option-clicked="optionClicked" />
    </template>
  </aside>
</template>

<style lang="scss">
@use '@/styles/utils';

.side-nav {
  padding: ads.$ads-spacing-scale-lg ads.$ads-spacing-scale-lg ads.$ads-spacing-scale-2xl;
  display: flex;
  flex-direction: column;
  gap: ads.$ads-spacing-scale-2xl;
  background: ads.$ads-surface;
  border-right: 1px solid ads.$ads-outline-low;
  height: 100vh;

  &__list {
    display: flex;
    flex-direction: column;
    gap: ads.$ads-spacing-scale-2xl;
    list-style: none;
    padding: 0;
    margin: 0;
    margin-top: ads.$ads-spacing-scale-2xl;

    .side-nav__list-item:first-child {
      margin-top: 0;
    }
  }

  &__list-item {
    @include ads-sass.input-02;
    border-radius: ads.$ads-border-radius-container-low;
    border: 1px solid transparent;
    color: ads.$ads-on-surface-mid;
  }

  .list-option-item__label {
    white-space: unset;
  }
}
</style>
